import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, darken, styled, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useParams, useSearchParams } from "react-router-dom";
import { ReportTemplateTypes } from "../../api/dtos";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import TitleBox from "../../components/TitleBox";
import ReportPreviewContext from "../../contexts/ReportPreviewContext";
import useReportPreviewById from "../../hooks/data/useReportPreviewById";
import useReportTemplateById from "../../hooks/data/useReportTemplateById";
import useReportTemplateFieldsByTemplateId from "../../hooks/data/useReportTemplateFieldsByTemplateId";
import useReportCustomTemplateFields from "../../hooks/data/useReportCustomTemplateFields";
import type { ReportColumnRow } from "./ReportColumnDefinitions";
import { buildSavedReportColumns, createReportTableRows } from "./ReportPreview.model";
import { readReportFilterParams, writeReportFilterParams } from "./ReportFilters.url";
import { AppFilterModel } from "../../components/tables/AppTable.types";

const HelpBox = styled(Box)(({ theme }) => ({
    backgroundColor: blue[100],
    borderWidth: "thick",
    borderColor: darken(blue[100], 0.1),
    borderRadius: 10,
    borderStyle: "solid",
    padding: theme.spacing(1),
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.secondary.main,
    textDecoration: "none",
    fontWeight: "bold",
}));

const ReportPreview: FC = () => {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const reportId = Number(params.reportId);

    const contextData = useReportPreviewById(reportId);
    const { data: preview } = contextData;
    const { data: template } = useReportTemplateById(preview?.report?.templateId);
    const { data: fields } = useReportTemplateFieldsByTemplateId(preview?.report?.templateId);

    const { data: fieldsWithNames } = useReportCustomTemplateFields();
    const rows = useMemo(() => createReportTableRows((preview?.rows ?? []) as ReportColumnRow[]), [preview?.rows]);
    const reportType = template?.reportTemplate?.templateType;
    const columns = useMemo(
        () => buildSavedReportColumns(reportType, rows.rows, reportId, fields, fieldsWithNames),
        [reportType, rows.rows, reportId, fields, fieldsWithNames],
    );

    const exportMetadata = useMemo(
        () => ({
            fileName: `${preview?.report?.name ?? "Unknown Report"} - Preview`,
            exportTitle: `Email Only Reports`,
            includeDateTime: true,
        }),
        [preview?.report?.name],
    );

    const currentSearch = searchParams.toString();
    const currentFilterCode = searchParams.get("f");
    const [localFilterEnvelope, setLocalFilterEnvelope] = useState<{
        reportId: number;
        filterCode: string | null;
        pendingFromCode?: string | null;
        model: AppFilterModel;
    } | null>(null);
    const filterModel =
        localFilterEnvelope?.reportId === reportId
            && (localFilterEnvelope.filterCode === currentFilterCode
                || ("pendingFromCode" in localFilterEnvelope && localFilterEnvelope.pendingFromCode === currentFilterCode))
            ? localFilterEnvelope.model
            : (readReportFilterParams(searchParams) ?? { filters: [], logic: "and" });

    useEffect(() => {
        if (localFilterEnvelope?.filterCode === currentFilterCode && "pendingFromCode" in localFilterEnvelope) {
            setLocalFilterEnvelope({
                reportId: localFilterEnvelope.reportId,
                filterCode: localFilterEnvelope.filterCode,
                model: localFilterEnvelope.model,
            });
        }
    }, [currentFilterCode, localFilterEnvelope]);

    const setFilterModel = (model: AppFilterModel) => {
        const nextParams = writeReportFilterParams(searchParams, model);
        const nextSearch = nextParams.toString();

        setLocalFilterEnvelope({
            reportId,
            filterCode: nextParams.get("f"),
            pendingFromCode: currentFilterCode,
            model,
        });

        if (nextSearch !== currentSearch) {
            setSearchParams(nextParams, { replace: true });
        }
    };

    return (
        <ReportPreviewContext.Provider value={contextData}>
            <SetLinks stageLinkEnabled={false} lotLinkEnabled={false} />
            <Outlet />
            <TitleBox
                sx={{
                    display: "flex",
                    // justifyContent: "flex-end",

                    gap: 2,
                }}
            >
                <HelpBox>
                    <FontAwesomeIcon icon={faQuestionCircle} size="lg" />
                    <Typography
                        sx={{
                            display: "inline",
                            ml: 2,
                        }}
                        variant="body1"
                    >
                        This is a saved report with filters already applied. You can adjust filters in real time by
                        viewing{" "}
                        <StyledLink to={`/reports/templates/preview/${template?.reportTemplate?.id}`}>
                            {template?.reportTemplate?.name ?? <>this template</>}
                        </StyledLink>
                        .
                    </Typography>
                </HelpBox>
                {template && template.reportTemplate?.templateType == ReportTemplateTypes.Assets && (
                    <StyledButton
                        variant="contained"
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={`/reports/preview/${reportId}/assets/create`}
                    >
                        Add Asset
                    </StyledButton>
                )}
            </TitleBox>
            <CollapsibleAppTable
                density="compact"
                title={"Entries"}
                loading={false}
                columns={columns}
                rows={rows.rows}
                getRowId={rows.getRowId}
                columnControls={{
                    menu: true,
                    visibility: true,
                    resize: true,
                }}
                filterControls
                export={exportMetadata}
                filterModel={filterModel}
                onFilterModelChange={(data) => setFilterModel(data)}
            />
        </ReportPreviewContext.Provider>
    );
};

export default ReportPreview;
